import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, Building2, CheckCircle2, Clock, FileUp, Loader2, Radio, RefreshCw, ShieldCheck, Upload, XCircle } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { API, studio, apiError, beDate, inputCls, btnPrimary, btnGhost, authHeaders } from "@/lib/studioApi";
import { PageSkeleton } from "@/components/studio/StudioUI";

const STATUS_STYLE = {
  none: "border-[#E2E7F7] bg-white text-[#5C6A87]",
  pending: "border-amber-200 bg-amber-50 text-amber-800",
  verified: "border-[#C9D4F4] bg-[#EEF1FC] text-[#3A5CC8]",
  active: "border-green-200 bg-green-50 text-green-700",
  rejected: "border-red-200 bg-red-50 text-red-700",
};
const STATUS_ICON = { none: Clock, pending: Clock, verified: BadgeCheck, active: Radio, rejected: XCircle };

export const PeppolStatusBadge = ({ status, label }) => {
  const Icon = STATUS_ICON[status] || Clock;
  return (
    <span data-testid="peppol-status-badge" className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.none}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
};

export default function StudioPeppol() {
  const [state, setState] = useState(null);
  const [vat, setVat] = useState("");
  const [vies, setVies] = useState(null);
  const [kind, setKind] = useState("bce");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);

  const load = () => studio.get("/peppol").then(({ data }) => { setState(data); if (data.vat && !vat) setVat(data.vat); }).catch((e) => toast.error(apiError(e, "Chargement impossible.")));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const checkVat = async () => {
    if (!vat.trim()) return toast.error("Indiquez votre numéro de TVA.");
    setBusy("vat");
    try {
      const { data } = await studio.get("/peppol/vat-check", { params: { vat } });
      setVies(data);
      if (!data.valid) toast.error("Numéro de TVA inconnu dans la base officielle VIES.");
    } catch (e) { toast.error(apiError(e, "Vérification impossible.")); }
    finally { setBusy(""); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("proof");
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", kind);
      const res = await fetch(`${API}/studio/peppol/proof`, { method: "POST", body: form, headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Dépôt impossible.");
      setProof(data);
      toast.success("Document déposé.");
    } catch (e) { toast.error(e.message || "Dépôt impossible."); }
    finally { setBusy(""); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!vies?.valid) return toast.error("Vérifiez d'abord votre numéro de TVA.");
    if (!proof) return toast.error("Déposez votre extrait BCE ou une facture de frais.");
    setBusy("submit");
    try {
      const { data } = await studio.post("/peppol/onboard", { vat: vies.vat, proof_media_id: proof.media_id, proof_kind: kind, proof_name: proof.name });
      setState(data);
      setVies(null);
      setProof(null);
      toast.success("Dossier soumis. La création de factures est débloquée.");
      load();
    } catch (e) { toast.error(apiError(e, "Soumission impossible.")); }
    finally { setBusy(""); }
  };

  if (!state) return <StudioLayout ownerOnly title="Peppol"><PageSkeleton /></StudioLayout>;

  const editable = state.status === "none" || state.status === "rejected";

  return (
    <StudioLayout ownerOnly title="Facturation électronique Peppol" subtitle="Étape obligatoire avant de créer vos factures : société vérifiée, puis envoi Peppol via le point d'accès Devisto.">
      <div data-testid="peppol-status-card" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Statut de votre société</p>
            <div className="mt-2"><PeppolStatusBadge status={state.status} label={state.status_label} /></div>
          </div>
          <button type="button" data-testid="peppol-refresh" onClick={load} className={btnGhost}><RefreshCw className="w-3.5 h-3.5" /> Actualiser</button>
        </div>
        {state.status !== "none" && (
          <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><dt className="text-xs text-[#5C6A87]">Société</dt><dd data-testid="peppol-company" className="font-semibold text-[#0F2645]">{state.company_name || "—"}</dd></div>
            <div><dt className="text-xs text-[#5C6A87]">TVA</dt><dd data-testid="peppol-vat" className="font-semibold text-[#0F2645]">{state.vat}</dd></div>
            <div><dt className="text-xs text-[#5C6A87]">Identifiant Peppol</dt><dd className="font-mono text-[#0F2645]">{state.peppol_address || "—"}</dd></div>
            <div><dt className="text-xs text-[#5C6A87]">Preuve déposée</dt><dd className="text-[#0F2645]">{state.proof_kind_label}{state.proof_name ? ` · ${state.proof_name}` : ""}</dd></div>
            <div><dt className="text-xs text-[#5C6A87]">Soumis le</dt><dd className="text-[#0F2645]">{beDate(state.submitted_at)}</dd></div>
            {state.validated_at && <div><dt className="text-xs text-[#5C6A87]">Validé le</dt><dd className="text-[#0F2645]">{beDate(state.validated_at)}</dd></div>}
          </dl>
        )}
        {state.status === "pending" && (
          <p data-testid="peppol-pending-hint" className="mt-4 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-800 leading-relaxed">
            Devisto vérifie votre document sous 24 h ouvrées. Vous pouvez déjà créer et envoyer vos factures par email ; l'envoi Peppol s'activera à la validation.
          </p>
        )}
        {state.status === "verified" && (
          <p data-testid="peppol-verified-hint" className="mt-4 rounded-xl bg-[#EEF1FC] border border-[#C9D4F4] px-4 py-3 text-xs text-[#3A5CC8] leading-relaxed">
            Votre société est vérifiée. Le point d'accès Peppol de Devisto est en cours d'activation : vos factures partent par email ou en fichier UBL en attendant.
          </p>
        )}
        {state.status === "active" && (
          <p data-testid="peppol-active-hint" className="mt-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-xs text-green-700 leading-relaxed">
            Le bouton « Envoyer via Peppol » est actif sur chaque facture d'un client professionnel enregistré sur le réseau.
          </p>
        )}
        {state.status === "rejected" && (
          <p data-testid="peppol-rejected-hint" className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-xs text-red-700 leading-relaxed">
            Motif : {state.rejected_reason}. Déposez un nouveau document ci-dessous.
          </p>
        )}
        {state.can_invoice && (
          <Link to="/studio/factures" data-testid="peppol-go-invoices" className={`${btnPrimary} mt-4`}>Aller à mes factures</Link>
        )}
      </div>

      {editable && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div data-testid="peppol-step-vat" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#3A5CC8]">Étape 1</p>
            <p className="mt-1 font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2"><Building2 className="w-4 h-4" /> Numéro de TVA</p>
            <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">Vérifié en direct dans la base officielle européenne VIES.</p>
            <div className="mt-3 flex gap-2">
              <input data-testid="peppol-vat-input" className={inputCls} placeholder="BE 0123.456.789" value={vat} onChange={(e) => { setVat(e.target.value); setVies(null); }} />
              <button type="button" data-testid="peppol-vat-check" disabled={busy === "vat"} onClick={checkVat} className={`${btnPrimary} whitespace-nowrap`}>
                {busy === "vat" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Vérifier
              </button>
            </div>
            {vies?.valid && (
              <div data-testid="peppol-vies-result" className="mt-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-xs text-green-800">
                <p className="inline-flex items-center gap-1.5 font-semibold"><CheckCircle2 className="w-3.5 h-3.5" /> {vies.name || "Société reconnue"}</p>
                <p className="mt-1">{vies.address} {vies.postal_code} {vies.city}</p>
                <p className="mt-1 font-mono">Peppol : {vies.peppol_address}</p>
              </div>
            )}
            {vies && !vies.valid && <p data-testid="peppol-vies-invalid" className="mt-3 text-xs font-semibold text-red-600">Numéro inconnu dans VIES.</p>}
          </div>

          <div data-testid="peppol-step-proof" className="rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)]">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#3A5CC8]">Étape 2</p>
            <p className="mt-1 font-heading text-base font-semibold text-[#0F2645] inline-flex items-center gap-2"><FileUp className="w-4 h-4" /> Preuve de la société</p>
            <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">Extrait BCE ou facture de frais au nom de la société (PDF ou photo, 15 Mo max).</p>
            <select data-testid="peppol-proof-kind" className={`${inputCls} mt-3`} value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="bce">Extrait BCE</option>
              <option value="facture">Facture de frais au nom de la société</option>
            </select>
            <input ref={fileRef} data-testid="peppol-proof-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
            <button type="button" data-testid="peppol-proof-upload" disabled={busy === "proof"} onClick={() => fileRef.current?.click()} className={`${btnGhost} mt-3`}>
              {busy === "proof" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {proof ? "Remplacer le document" : "Déposer le document"}
            </button>
            {proof && <p data-testid="peppol-proof-name" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-green-700"><CheckCircle2 className="w-3.5 h-3.5" /> {proof.name}</p>}
          </div>

          <div data-testid="peppol-step-submit" className="rounded-2xl border border-[#0F2645] bg-[#0F2645] p-5 text-white shadow-[0_2px_6px_rgba(15,38,69,0.08)]">
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#93A0BC]">Étape 3</p>
            <p className="mt-1 font-heading text-base font-semibold inline-flex items-center gap-2"><Radio className="w-4 h-4" /> Activer la facturation</p>
            <ul className="mt-3 space-y-1.5 text-xs text-[#C9D4F4] leading-relaxed">
              <li>· Création de factures débloquée immédiatement</li>
              <li>· Vérification du document par Devisto sous 24 h ouvrées</li>
              <li>· Envoi Peppol via le point d'accès unique Devisto</li>
              <li>· Connexion itsme : bientôt, dès l'agrément partenaire</li>
            </ul>
            <button type="button" data-testid="peppol-submit" disabled={busy === "submit" || !vies?.valid || !proof} onClick={submit} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-[#0F2645] transition-colors duration-200 hover:bg-[#EEF1FC] disabled:opacity-50">
              {busy === "submit" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Soumettre mon dossier
            </button>
          </div>
        </div>
      )}
    </StudioLayout>
  );
}
